import { useEffect, useCallback, useRef } from 'react';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  action: () => void;
  description: string;
  preventDefault?: boolean;
}

interface UseEnhancedKeyboardNavigationProps {
  shortcuts?: KeyboardShortcut[];
  enableGlobalShortcuts?: boolean;
  enableFocusManagement?: boolean;
  enableSkipLinks?: boolean;
  onNavigate?: (direction: 'up' | 'down' | 'left' | 'right') => void;
  onActivate?: () => void;
  onCancel?: () => void;
  onHelp?: () => void;
}

export const useEnhancedKeyboardNavigation = ({
  shortcuts = [],
  enableGlobalShortcuts = true,
  enableFocusManagement = true,
  enableSkipLinks = true,
  onNavigate,
  onActivate,
  onCancel,
  onHelp
}: UseEnhancedKeyboardNavigationProps = {}) => {
  const shortcutsRef = useRef<KeyboardShortcut[]>(shortcuts);
  const helpDialogRef = useRef<HTMLDialogElement | null>(null);

  // Update shortcuts ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  // Default keyboard shortcuts
  const defaultShortcuts: KeyboardShortcut[] = [
    {
      key: 'Escape',
      action: () => onCancel?.(),
      description: 'Cancel or close current action',
      preventDefault: true
    },
    {
      key: 'Enter',
      action: () => onActivate?.(),
      description: 'Activate or confirm current action',
      preventDefault: true
    },
    {
      key: 'ArrowUp',
      action: () => onNavigate?.('up'),
      description: 'Navigate up',
      preventDefault: true
    },
    {
      key: 'ArrowDown',
      action: () => onNavigate?.('down'),
      description: 'Navigate down',
      preventDefault: true
    },
    {
      key: 'ArrowLeft',
      action: () => onNavigate?.('left'),
      description: 'Navigate left',
      preventDefault: true
    },
    {
      key: 'ArrowRight',
      action: () => onNavigate?.('right'),
      description: 'Navigate right',
      preventDefault: true
    },
    {
      key: 'h',
      ctrlKey: true,
      action: () => onHelp?.(),
      description: 'Show keyboard shortcuts help',
      preventDefault: true
    },
    {
      key: '?',
      action: () => onHelp?.(),
      description: 'Show keyboard shortcuts help',
      preventDefault: true
    }
  ];

  // Focus management utilities
  const focusNext = useCallback(() => {
    const focusableElements = document.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
    ) as NodeListOf<HTMLElement>;
    
    const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as HTMLElement);
    const nextIndex = (currentIndex + 1) % focusableElements.length;
    focusableElements[nextIndex]?.focus();
  }, []);

  const focusPrevious = useCallback(() => {
    const focusableElements = document.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
    ) as NodeListOf<HTMLElement>;
    
    const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as HTMLElement);
    const previousIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    focusableElements[previousIndex]?.focus();
  }, []);

  // Skip to main content
  const skipToMain = useCallback(() => {
    const mainContent = document.querySelector('main, [role="main"], #main-content');
    if (mainContent) {
      (mainContent as HTMLElement).focus();
      (mainContent as HTMLElement).scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Skip to navigation
  const skipToNavigation = useCallback(() => {
    const navigation = document.querySelector('nav, [role="navigation"]');
    if (navigation) {
      (navigation as HTMLElement).focus();
      (navigation as HTMLElement).scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Enhanced shortcuts with focus management
  const enhancedShortcuts: KeyboardShortcut[] = [
    ...defaultShortcuts,
    ...shortcuts,
    {
      key: 'Tab',
      action: focusNext,
      description: 'Move to next focusable element',
      preventDefault: false
    },
    {
      key: 'Tab',
      shiftKey: true,
      action: focusPrevious,
      description: 'Move to previous focusable element',
      preventDefault: false
    },
    {
      key: 'm',
      ctrlKey: true,
      action: skipToMain,
      description: 'Skip to main content',
      preventDefault: true
    },
    {
      key: 'n',
      ctrlKey: true,
      action: skipToNavigation,
      description: 'Skip to navigation',
      preventDefault: true
    }
  ];

  // Handle keyboard events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const allShortcuts = [...enhancedShortcuts, ...shortcutsRef.current];
    
    for (const shortcut of allShortcuts) {
      const keyMatch = event.key === shortcut.key;
      const ctrlMatch = !!shortcut.ctrlKey === event.ctrlKey;
      const shiftMatch = !!shortcut.shiftKey === event.shiftKey;
      const altMatch = !!shortcut.altKey === event.altKey;
      const metaMatch = !!shortcut.metaKey === event.metaKey;

      if (keyMatch && ctrlMatch && shiftMatch && altMatch && metaMatch) {
        if (shortcut.preventDefault !== false) {
          event.preventDefault();
        }
        shortcut.action();
        break;
      }
    }
  }, [enhancedShortcuts]);

  // Register keyboard event listeners
  useEffect(() => {
    if (enableGlobalShortcuts) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, enableGlobalShortcuts]);

  // Create skip links
  useEffect(() => {
    if (enableSkipLinks) {
      const skipLinksContainer = document.getElementById('skip-links');
      if (!skipLinksContainer) {
        const container = document.createElement('div');
        container.id = 'skip-links';
        container.className = 'sr-only';
        container.innerHTML = `
          <a href="#main-content" class="skip-link" onclick="document.querySelector('#main-content')?.focus()">Skip to main content</a>
          <a href="#navigation" class="skip-link" onclick="document.querySelector('#navigation')?.focus()">Skip to navigation</a>
        `;
        document.body.insertBefore(container, document.body.firstChild);
      }
    }
  }, [enableSkipLinks]);

  // Show keyboard shortcuts help
  const showKeyboardHelp = useCallback(() => {
    const allShortcuts = [...enhancedShortcuts, ...shortcutsRef.current];
    const helpContent = allShortcuts
      .map(shortcut => {
        const keys = [];
        if (shortcut.ctrlKey) keys.push('Ctrl');
        if (shortcut.shiftKey) keys.push('Shift');
        if (shortcut.altKey) keys.push('Alt');
        if (shortcut.metaKey) keys.push('Cmd');
        keys.push(shortcut.key);
        
        return `${keys.join(' + ')}: ${shortcut.description}`;
      })
      .join('\n');

    // Create or update help dialog
    let helpDialog = helpDialogRef.current;
    if (!helpDialog) {
      helpDialog = document.createElement('dialog');
      helpDialog.className = 'keyboard-help-dialog';
      helpDialog.innerHTML = `
        <div class="keyboard-help-content">
          <h2>Keyboard Shortcuts</h2>
          <pre>${helpContent}</pre>
          <button onclick="this.closest('dialog').close()">Close</button>
        </div>
      `;
      document.body.appendChild(helpDialog);
      helpDialogRef.current = helpDialog;
    }
    
    helpDialog.showModal();
  }, [enhancedShortcuts]);

  return {
    focusNext,
    focusPrevious,
    skipToMain,
    skipToNavigation,
    showKeyboardHelp,
    shortcuts: enhancedShortcuts
  };
};

export default useEnhancedKeyboardNavigation;
